import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  TrendingUp, TrendingDown, Clock, CheckCircle2, Truck,
  Download, FileText,
} from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Cell, Legend,
} from 'recharts';
import PageHeader from '@/components/ui/PageHeader';
import DataTable, { Column } from '@/components/ui/DataTable';
import PillBadge from '@/components/ui/PillBadge';
import { ChartSkeleton, CardSkeleton } from '@/components/ui/LoadingSkeleton';
import EmptyState from '@/components/ui/EmptyState';
import api from '@/lib/api';
import { useTableExport } from '@/hooks/useTableExport';

// ─── Types ────────────────────────────────────────────────────────────────────

interface KpiCard {
  label: string;
  value: number;
  unit: string;
  prev: number;
  spark: number[];
}

interface YieldTrend {
  month: string;
  yield: number;
  target: number;
}

interface DefectByCategory {
  category: string;
  count: number;
}

interface TatDistribution {
  bucket: string;
  count: number;
}

interface TestVolumeLab {
  lab: string;
  passed: number;
  failed: number;
}

interface MetricsPayload {
  kpis: KpiCard[];
  yieldTrend: YieldTrend[];
  defectByCategory: DefectByCategory[];
  tatDistribution: TatDistribution[];
  testVolumeByLab: TestVolumeLab[];
}

interface QualityEvent {
  id: string;
  date: string;
  lab: string;
  metric: string;
  value: number;
  target: number;
  status: 'met' | 'missed';
  category: string;
  [key: string]: unknown;
}

// ─── Mock Fallbacks ───────────────────────────────────────────────────────────

const MOCK_KPIS: KpiCard[] = [
  { label: 'First-Pass Yield', value: 94.2, unit: '%', prev: 91.8, spark: [88, 90, 91, 93, 94, 94.2] },
  { label: 'Avg TAT', value: 2.4, unit: 'days', prev: 2.9, spark: [3.2, 3.0, 2.8, 2.7, 2.5, 2.4] },
  { label: 'Defect Rate', value: 1.8, unit: '%', prev: 2.3, spark: [3.1, 2.8, 2.5, 2.2, 2.0, 1.8] },
  { label: 'On-Time Delivery', value: 97.1, unit: '%', prev: 95.4, spark: [92, 93, 95, 96, 96.5, 97.1] },
];

const MOCK_YIELD_TREND: YieldTrend[] = [
  { month: 'Jul', yield: 88.4, target: 92 },
  { month: 'Aug', yield: 90.1, target: 92 },
  { month: 'Sep', yield: 91.7, target: 92 },
  { month: 'Oct', yield: 92.8, target: 92 },
  { month: 'Nov', yield: 93.5, target: 92 },
  { month: 'Dec', yield: 94.2, target: 92 },
];

const MOCK_DEFECTS: DefectByCategory[] = [
  { category: 'Labeling', count: 42 },
  { category: 'Potency', count: 31 },
  { category: 'Dissolution', count: 24 },
  { category: 'Sterility', count: 18 },
  { category: 'Packaging', count: 11 },
];

const MOCK_TAT: TatDistribution[] = [
  { bucket: '1d', count: 58 },
  { bucket: '2d', count: 134 },
  { bucket: '3d', count: 97 },
  { bucket: '4d', count: 43 },
  { bucket: '5d+', count: 21 },
];

const MOCK_VOLUME: TestVolumeLab[] = [
  { lab: 'Lab A', passed: 312, failed: 18 },
  { lab: 'Lab B', passed: 287, failed: 24 },
  { lab: 'Lab C', passed: 198, failed: 9 },
  { lab: 'Lab D', passed: 145, failed: 31 },
];

const MOCK_EVENTS: QualityEvent[] = [
  { id: '1', date: '2026-04-01', lab: 'Lab A', metric: 'First-Pass Yield', value: 93.1, target: 92, status: 'met', category: 'Yield' },
  { id: '2', date: '2026-04-01', lab: 'Lab B', metric: 'Defect Rate', value: 2.9, target: 2.0, status: 'missed', category: 'Defect' },
  { id: '3', date: '2026-03-31', lab: 'Lab C', metric: 'TAT', value: 1.8, target: 3.0, status: 'met', category: 'TAT' },
  { id: '4', date: '2026-03-30', lab: 'Lab A', metric: 'On-Time Delivery', value: 98.4, target: 95, status: 'met', category: 'Delivery' },
  { id: '5', date: '2026-03-29', lab: 'Lab D', metric: 'Defect Rate', value: 4.1, target: 2.0, status: 'missed', category: 'Defect' },
  { id: '6', date: '2026-03-28', lab: 'Lab B', metric: 'First-Pass Yield', value: 91.0, target: 92, status: 'missed', category: 'Yield' },
];

const DEFECT_COLORS = ['#DC2626', '#D97706', '#2563EB', '#7C3AED', '#b07d1a'];
const RANGE_OPTIONS = ['3M', '6M', '12M'] as const;
type Range = typeof RANGE_OPTIONS[number];

// ─── Sparkline ────────────────────────────────────────────────────────────────

function MiniSparkline({ data, color }: { data: number[]; color: string }) {
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const h = 32;
  const w = 80;
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * w;
    const y = h - ((v - min) / range) * h;
    return `${x},${y}`;
  });
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="overflow-visible">
      <polyline points={pts.join(' ')} fill="none" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ─── KPI Card ─────────────────────────────────────────────────────────────────

function KpiTile({ card }: { card: KpiCard }) {
  const improvingMetric = card.label !== 'Avg TAT' && card.label !== 'Defect Rate';
  const improved = improvingMetric ? card.value > card.prev : card.value < card.prev;
  const delta = Math.abs(card.value - card.prev).toFixed(1);
  const color = improved ? '#16A34A' : '#DC2626';

  return (
    <div className="card p-5 flex flex-col gap-3">
      <p className="text-xs font-semibold text-text-muted uppercase tracking-wide">{card.label}</p>
      <div className="flex items-end justify-between">
        <div>
          <span className="font-serif font-bold text-3xl text-text">{card.value}</span>
          <span className="text-sm text-text-muted ml-1">{card.unit}</span>
        </div>
        <MiniSparkline data={card.spark} color={color} />
      </div>
      <div className="flex items-center gap-1">
        {improved
          ? <TrendingUp size={14} style={{ color }} />
          : <TrendingDown size={14} style={{ color }} />}
        <span className="text-xs font-semibold" style={{ color }}>
          {improved ? '+' : '-'}{delta} {card.unit} vs prev period
        </span>
      </div>
    </div>
  );
}

// ─── Event Table Columns ──────────────────────────────────────────────────────

const EVENT_COLUMNS: Column<QualityEvent>[] = [
  { key: 'date', label: 'Date', render: (r) => <span className="text-xs text-text-muted">{r.date}</span> },
  { key: 'lab', label: 'Lab' },
  { key: 'metric', label: 'Metric' },
  { key: 'value', label: 'Value', render: (r) => <span className="font-mono">{r.value}</span> },
  { key: 'target', label: 'Target', render: (r) => <span className="font-mono text-text-muted">{r.target}</span> },
  {
    key: 'status', label: 'Status',
    render: (r) => <PillBadge value={r.status === 'met' ? 'completed' : 'overdue'} label={r.status === 'met' ? 'Met' : 'Missed'} />,
  },
  { key: 'category', label: 'Category', render: (r) => <PillBadge value="minor" label={r.category} /> },
];

const EXPORT_COLS = EVENT_COLUMNS.map((c) => ({ key: c.key as string, label: c.label }));

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function QualityMetrics() {
  const [range, setRange] = useState<Range>('6M');
  const [labId, setLabId] = useState('');

  const { data: metricsData, isLoading: metricsLoading } = useQuery<MetricsPayload>({
    queryKey: ['quality-metrics', { range, labId }],
    queryFn: () =>
      api.get('/quality-metrics', { params: { range, ...(labId && { labId }) } }).then((r) => r.data),
    staleTime: 300_000,
  });

  const { data: trendData, isLoading: trendLoading } = useQuery<{ events: QualityEvent[] }>({
    queryKey: ['quality-metrics-trend', { range, labId }],
    queryFn: () =>
      api.get('/quality-metrics/trend', { params: { range, ...(labId && { labId }) } }).then((r) => r.data),
    staleTime: 300_000,
  });

  const kpis = metricsData?.kpis ?? MOCK_KPIS;
  const yieldTrend = metricsData?.yieldTrend ?? MOCK_YIELD_TREND;
  const defects = metricsData?.defectByCategory ?? MOCK_DEFECTS;
  const tat = metricsData?.tatDistribution ?? MOCK_TAT;
  const volume = metricsData?.testVolumeByLab ?? MOCK_VOLUME;
  const events = (trendData?.events ?? MOCK_EVENTS) as unknown as Record<string, unknown>[];

  const { exportCSV, exportPDF } = useTableExport(events, EXPORT_COLS, 'quality-metrics');

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        title="Quality Metrics"
        subtitle="First-pass yield · Turnaround time · Defect rate trending"
        actions={
          <>
            <select
              className="text-sm border border-border rounded-lg px-3 py-2 bg-card text-text"
              value={labId}
              onChange={(e) => setLabId(e.target.value)}
            >
              <option value="">All Labs</option>
              <option value="lab-a">Lab A</option>
              <option value="lab-b">Lab B</option>
              <option value="lab-c">Lab C</option>
              <option value="lab-d">Lab D</option>
            </select>
            <div className="flex rounded-lg border border-border overflow-hidden">
              {RANGE_OPTIONS.map((r) => (
                <button
                  key={r}
                  onClick={() => setRange(r)}
                  className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                    range === r ? 'bg-teal-600 text-white' : 'bg-card text-text-muted hover:bg-bg'
                  }`}
                >
                  {r}
                </button>
              ))}
            </div>
            <button onClick={exportCSV} className="btn-ghost flex items-center gap-2 text-sm">
              <Download size={14} /> CSV
            </button>
            <button onClick={exportPDF} className="btn-ghost flex items-center gap-2 text-sm">
              <FileText size={14} /> PDF
            </button>
          </>
        }
      />

      {/* KPI Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {metricsLoading
          ? Array.from({ length: 4 }).map((_, i) => <CardSkeleton key={i} />)
          : kpis.map((k) => <KpiTile key={k.label} card={k} />)}
      </div>

      {/* Charts 2x2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 1 — Monthly Yield Trend */}
        {trendLoading ? (
          <ChartSkeleton height={220} />
        ) : (
          <div className="card p-5">
            <p className="font-serif font-semibold text-sm text-text mb-1">Monthly First-Pass Yield</p>
            <p className="text-xs text-text-muted mb-4">vs target line</p>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={yieldTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis domain={[80, 100]} tick={{ fontSize: 11 }} unit="%" />
                <Tooltip formatter={(v: number) => `${v}%`} />
                <Legend />
                <Line type="monotone" dataKey="yield" stroke="#16A34A" strokeWidth={2.5} dot={{ r: 3 }} name="Yield %" />
                <Line type="monotone" dataKey="target" stroke="#94a3b8" strokeWidth={1.5} strokeDasharray="4 4" dot={false} name="Target %" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* 2 — Defect Rate by Category */}
        {metricsLoading ? (
          <ChartSkeleton height={220} />
        ) : (
          <div className="card p-5">
            <p className="font-serif font-semibold text-sm text-text mb-1">Defect Rate by Category</p>
            <p className="text-xs text-text-muted mb-4">count of non-conformances</p>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={defects} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis dataKey="category" type="category" tick={{ fontSize: 11 }} width={80} />
                <Tooltip />
                <Bar dataKey="count" name="Count" radius={[0, 4, 4, 0]}>
                  {defects.map((_, i) => (
                    <Cell key={i} fill={DEFECT_COLORS[i % DEFECT_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* 3 — TAT Distribution */}
        {metricsLoading ? (
          <ChartSkeleton height={220} />
        ) : (
          <div className="card p-5">
            <p className="font-serif font-semibold text-sm text-text mb-1">Turnaround Time Distribution</p>
            <p className="text-xs text-text-muted mb-4">number of tests per TAT bucket</p>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={tat}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="count" fill="#2563EB" radius={[4, 4, 0, 0]} name="Tests" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* 4 — Test Volume by Lab (stacked) */}
        {metricsLoading ? (
          <ChartSkeleton height={220} />
        ) : (
          <div className="card p-5">
            <p className="font-serif font-semibold text-sm text-text mb-1">Test Volume by Lab</p>
            <p className="text-xs text-text-muted mb-4">passed vs failed</p>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={volume}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="lab" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="passed" stackId="a" fill="#16A34A" radius={[0, 0, 0, 0]} name="Passed" />
                <Bar dataKey="failed" stackId="a" fill="#DC2626" radius={[4, 4, 0, 0]} name="Failed" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Recent Quality Events */}
      <div>
        <h2 className="font-serif font-semibold text-base text-text mb-3">Recent Quality Events</h2>
        <DataTable<QualityEvent>
          columns={EVENT_COLUMNS}
          data={(trendData?.events ?? MOCK_EVENTS) as QualityEvent[]}
          loading={trendLoading}
          keyField="id"
          pageSize={10}
          emptyMessage="No quality events found for this period."
          tableId="quality-events"
        />
      </div>
    </div>
  );
}
